/**
 * useSpeechToText — Dictado por voz continuo (Web Speech API), INDEPENDIENTE de
 * LiveKit, estilo "conversación" para el copiloto.
 *
 * Captura el micrófono del médico y transcribe en el browser. Funciona como el
 * modo de voz de los asistentes tipo Claude:
 *   - El médico activa el micrófono y habla.
 *   - `onInterim(textoParcial)` entrega la transcripción EN VIVO de la frase
 *     actual (para mostrarla en el input que va creciendo).
 *   - En cada PAUSA natural el reconocedor finaliza la frase y se emite
 *     `onSegment(frase)`: el copiloto la procesa mientras el médico SIGUE
 *     hablando (la escucha no se detiene), manteniendo el contexto como una
 *     conversación.
 *   - La escucha continúa hasta que el médico la detiene (`stop`).
 *
 * Requiere Chrome/Edge/Safari + internet (la API envía audio a los servidores
 * del navegador para transcribir).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

interface SpeechRecognitionResult {
  isFinal: boolean;
  0: { transcript: string; confidence: number };
}
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResult> & {
    [index: number]: SpeechRecognitionResult;
    length: number;
  };
}
interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: (e: SpeechRecognitionEvent) => void;
  onerror: (e: Event) => void;
  onend: () => void;
}
interface SpeechRecognitionConstructor { new (): SpeechRecognitionInstance }

function getCtor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

interface UseSpeechToTextOptions {
  lang?: string;
  /** Transcripción parcial en vivo de la frase en curso. */
  onInterim?: (interim: string) => void;
  /** Frase finalizada (en cada pausa). Se procesa y se sigue escuchando. */
  onSegment?: (segment: string) => void;
}

export interface UseSpeechToTextReturn {
  supported: boolean;
  listening: boolean;
  error: string | null;
  start: () => void;
  stop: () => void;
  toggle: () => void;
}

export function useSpeechToText({
  lang = 'es-PA',
  onInterim,
  onSegment,
}: UseSpeechToTextOptions = {}): UseSpeechToTextReturn {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supported = !!getCtor();

  const recRef = useRef<SpeechRecognitionInstance | null>(null);
  const wantRef = useRef(false);        // el usuario quiere seguir escuchando
  const manualStopRef = useRef(false);  // detención explícita
  const restartTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onInterimRef = useRef(onInterim);
  const onSegmentRef = useRef(onSegment);
  onInterimRef.current = onInterim;
  onSegmentRef.current = onSegment;

  const buildAndStart = useCallback(function doBuildAndStart() {
    const Ctor = getCtor();
    if (!Ctor) return;
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = lang;
    recRef.current = rec;

    rec.onresult = (e: SpeechRecognitionEvent) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        const tx = (r[0]?.transcript ?? '').trim();
        if (!tx) continue;
        if (r.isFinal) {
          // Pausa → frase finalizada: se procesa y se sigue escuchando.
          onSegmentRef.current?.(tx);
        } else {
          interim = `${interim} ${tx}`.trim();
        }
      }
      onInterimRef.current?.(interim);
    };

    rec.onerror = (e: Event) => {
      const er = (e as Event & { error?: string }).error ?? 'unknown';
      if (er === 'not-allowed' || er === 'service-not-allowed') {
        setError('Permiso de micrófono denegado.');
        wantRef.current = false;
      } else if (er !== 'no-speech' && er !== 'aborted') {
        setError('Error de reconocimiento de voz.');
      }
    };

    rec.onend = () => {
      if (manualStopRef.current) {
        manualStopRef.current = false;
        wantRef.current = false;
        setListening(false);
        return;
      }
      // Fin por silencio/timeout: reiniciar para mantener la escucha activa.
      if (wantRef.current) {
        if (restartTimer.current) clearTimeout(restartTimer.current);
        restartTimer.current = setTimeout(() => { if (wantRef.current) doBuildAndStart(); }, 300);
      } else {
        setListening(false);
      }
    };

    try {
      rec.start();
    } catch {
      if (restartTimer.current) clearTimeout(restartTimer.current);
      restartTimer.current = setTimeout(() => { if (wantRef.current) doBuildAndStart(); }, 500);
    }
  }, [lang]);

  const start = useCallback(() => {
    if (!supported) { setError('Tu navegador no soporta dictado por voz.'); return; }
    manualStopRef.current = false;
    wantRef.current = true;
    setError(null);
    setListening(true);
    buildAndStart();
  }, [supported, buildAndStart]);

  const stop = useCallback(() => {
    manualStopRef.current = true;
    wantRef.current = false;
    if (restartTimer.current) clearTimeout(restartTimer.current);
    try { recRef.current?.stop(); } catch { /* ignore */ }
  }, []);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  useEffect(() => () => {
    wantRef.current = false;
    if (restartTimer.current) clearTimeout(restartTimer.current);
    try { recRef.current?.abort(); } catch { /* ignore */ }
  }, []);

  return { supported, listening, error, start, stop, toggle };
}
